import { useState } from 'react';
import { t, tn } from '@app/i18n';
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  ErrorBanner,
  Field,
  FieldGrid,
  IconButton,
  LoadingButton,
  TextInput,
  type DataTableColumn,
} from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import { useIdentityRoles } from '../Identities/Identities.api';
import {
  useCreateIdentityRoleT21,
  useDeleteIdentityRole,
  useUpdateIdentityRole,
} from './TenantSettings.api';
import type { IdentityRole } from './TenantSettingsModel';
import s from './TenantSettings.module.css';

/**
 * Card **Papéis das identidades** de T21 (FR-052, FR-118, FR-130) — US14, T427.
 *
 * Lista com quantidade de identidades e origem (manual / automático — badge),
 * criar (nome + descrição), editar a descrição inline, excluir com `ConfirmDialog`
 * — e o bloqueio `papel_em_uso` com a contagem quando há identidades. Approver e
 * viewer só leem (14.10): os botões são omitidos, não desabilitados (FR-010).
 */
export function IdentityRolesCard() {
  const roleList = useIdentityRoles();
  const canCreate = useCanOperar('createIdentityRole');
  const canEdit = useCanOperar('updateIdentityRole');
  const canDelete = useCanOperar('deleteIdentityRole');

  const create = useCreateIdentityRoleT21();
  const edit = useUpdateIdentityRole();
  const remove = useDeleteIdentityRole();

  const [criando, setCriando] = useState(false);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<IdentityRole | null>(null);

  const columns: DataTableColumn<IdentityRole>[] = [
    {
      key: 'name',
      header: t('tenantSettings.roles.column.role'),
      mono: true,
      cell: (p) => p.name,
      width: '18%',
    },
    {
      key: 'description',
      header: t('tenantSettings.roles.column.description'),
      cell: (p) =>
        editandoId === p.id ? (
          <DescriptionInline
            role={p}
            pending={edit.isPending}
            onCancel={() => setEditandoId(null)}
            onSave={(description) =>
              edit.mutate(
                { id: p.id, body: { description } },
                { onSuccess: () => setEditandoId(null) },
              )
            }
          />
        ) : (
          <span className={s.description}>
            <span>
              {p.description ?? (
                <em className={s.semDescricao}>{t('tenantSettings.roles.noDescription')}</em>
              )}
            </span>
            {/* Fica NA célula da descrição, e não na coluna de ações: o que ele abre é
                o campo desta célula. Movê-lo para as ações afastaria o gatilho do campo
                que ele edita. */}
            {canEdit ? (
              <IconButton
                icon="pen-square"
                size="sm"
                label={t('tenantSettings.roles.edit.label', { name: p.name })}
                onClick={() => setEditandoId(p.id)}
              />
            ) : null}
          </span>
        ),
    },
    {
      key: 'origin',
      header: t('tenantSettings.roles.column.origin'),
      tip: t('tenantSettings.roles.column.origin.tip'),
      // `origin` é enum do contrato; o que a Badge pinta é rótulo de tela.
      cell: (p) =>
        p.origin === 'automatico' ? (
          <Badge tone="primary">{t('tenantSettings.roles.origin.automatic')}</Badge>
        ) : (
          <Badge tone="neutral">{t('tenantSettings.roles.origin.manual')}</Badge>
        ),
      width: '14%',
    },
    {
      key: 'identities',
      header: t('tenantSettings.roles.column.identities'),
      numeric: true,
      cell: (p) => p.identities,
      width: '12%',
    },
    ...(canDelete
      ? [
          {
            key: 'actions',
            header: t('tenantSettings.roles.column.actions'),
            srOnlyHeader: true,
            width: '8%',
            cell: (p: IdentityRole) => (
              <IconButton
                icon="trash"
                size="sm"
                danger
                label={t('tenantSettings.roles.delete.label', { name: p.name })}
                onClick={() => {
                  remove.reset();
                  setExcluindo(p);
                }}
              />
            ),
          } satisfies DataTableColumn<IdentityRole>,
        ]
      : []),
  ];

  return (
    <Card
      title={t('tenantSettings.roles.title')}
      subtitle={t('tenantSettings.roles.subtitle')}
      toolbar={
        canCreate && !criando ? (
          <Button variant="out" icon="plus" onClick={() => setCriando(true)}>
            {t('tenantSettings.roles.new')}
          </Button>
        ) : null
      }
    >
      {edit.error ? (
        <ErrorBanner
          error={edit.error}
          escrita
          title={t('tenantSettings.roles.error.edit')}
          hideLink
        />
      ) : null}

      {criando ? (
        <NewRole
          pending={create.isPending}
          error={create.error}
          onCancel={() => {
            create.reset();
            setCriando(false);
          }}
          onCreate={(payload) =>
            create.mutate(payload, {
              onSuccess: () => setCriando(false),
            })
          }
        />
      ) : null}

      <DataTable<IdentityRole>
        label={t('tenantSettings.roles.table.label')}
        columns={columns}
        rows={roleList.data ?? []}
        rowKey={(p) => p.id}
        loading={roleList.isLoading}
        error={roleList.error}
        onRetry={() => void roleList.refetch()}
        emptyTitle={t('tenantSettings.roles.empty.title')}
        emptyMessage={t('tenantSettings.roles.empty.message')}
      />

      <ConfirmDialog
        open={excluindo !== null}
        title={t('tenantSettings.roles.confirm.title', { name: excluindo?.name ?? '' })}
        message={
          excluindo && excluindo.identities > 0
            ? // «identidade(s)» era o que estava escrito; o `tn()` dá a forma certa.
              tn('tenantSettings.roles.confirm.inUse', excluindo.identities)
            : t('tenantSettings.roles.confirm.free')
        }
        destructive
        confirmLabel={t('tenantSettings.roles.confirm.action')}
        confirmDisabled={Boolean(excluindo && excluindo.identities > 0)}
        loading={remove.isPending}
        error={
          remove.error ? (
            <ErrorBanner
              error={remove.error}
              escrita
              title={t('tenantSettings.roles.confirm.error')}
              hideLink
            />
          ) : null
        }
        onCancel={() => {
          setExcluindo(null);
          remove.reset();
        }}
        onConfirm={() => {
          if (!excluindo) return;
          remove.mutate(excluindo.id, { onSuccess: () => setExcluindo(null) });
        }}
      />

      {remove.isSuccess && !excluindo ? (
        <Alert tone="info" title={t('tenantSettings.roles.deleted')} />
      ) : null}
    </Card>
  );
}

function DescriptionInline({
  role,
  pending,
  onSave,
  onCancel,
}: {
  role: IdentityRole;
  pending: boolean;
  onSave: (description: string | null) => void;
  onCancel: () => void;
}) {
  const [valor, setValor] = useState(role.description ?? '');
  const id = `descricao-${role.id}`;
  return (
    <span className={s.descricaoEdicao}>
      <label htmlFor={id} className={s.srOnly}>
        {t('tenantSettings.roles.inline.label', { name: role.name })}
      </label>
      <TextInput
        id={id}
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSave(valor.trim() || null);
          }
          if (e.key === 'Escape') onCancel();
        }}
      />
      <LoadingButton
        size="sm"
        loading={pending}
        onClick={() => onSave(valor.trim() || null)}
        aria-label={t('tenantSettings.roles.inline.save.label', { name: role.name })}
      >
        {t('tenantSettings.roles.inline.save')}
      </LoadingButton>
      <Button size="sm" variant="soft" onClick={onCancel}>
        {t('tenantSettings.roles.inline.cancel')}
      </Button>
    </span>
  );
}

function NewRole({
  pending,
  error,
  onCreate,
  onCancel,
}: {
  pending: boolean;
  error: import('@app/api/ApiError').ApiError | null;
  onCreate: (payload: { name: string; description: string | null }) => void;
  onCancel: () => void;
}) {
  const [nome, setNome] = useState('');
  const [description, setDescricao] = useState('');
  const validName = /^[a-z0-9-]+$/.test(nome.trim());

  return (
    <form
      className={s.novoPapel}
      aria-label={t('tenantSettings.roles.form.label')}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (!validName) return;
        onCreate({ name: nome.trim(), description: description.trim() || null });
      }}
    >
      {error ? (
        <ErrorBanner error={error} escrita title={t('tenantSettings.roles.form.error')} hideLink />
      ) : null}
      <FieldGrid>
        <Field
          label={t('tenantSettings.roles.form.name')}
          span={6}
          required
          error={nome && !validName ? t('tenantSettings.roles.form.name.error') : null}
          tip={t('tenantSettings.roles.form.name.tip')}
        >
          {(props) => (
            <TextInput {...props} mono value={nome} onChange={(e) => setNome(e.target.value)} />
          )}
        </Field>
        <Field label={t('tenantSettings.roles.form.description')} span={6}>
          {(props) => (
            <TextInput
              {...props}
              value={description}
              onChange={(e) => setDescricao(e.target.value)}
            />
          )}
        </Field>
      </FieldGrid>
      <div className={s.acoes}>
        <Button variant="soft" onClick={onCancel} type="button">
          {t('tenantSettings.roles.form.cancel')}
        </Button>
        <LoadingButton type="submit" loading={pending} disabled={!validName}>
          {t('tenantSettings.roles.form.submit')}
        </LoadingButton>
      </div>
    </form>
  );
}
